import './Cards.css';

import { IonCard, IonCardContent, useIonViewWillEnter } from '@ionic/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { DqText } from 'src/components/dq-text';
import { useSocket } from 'src/utils/socket';
import sentinelLogo from '../../../assets/images/SentinelLogo.png';
import { GenericMessages } from '../../../intl/GenericMessages';
import { JobMessages } from '../../../intl/JobMessages';
import { JobLog, LogSearchResult } from '../../../models/jobLog';
import { AppRouteConstants } from '../../../pages/app-layout/AppRouteConstants';
import { AuthUserInfo } from '../../../redux/auth/types';
import { useParsedLog } from '../../../redux/log/thunk/fetchLog';
import { SocketContextSentinel } from '../../../utils/socket/context/sentinelSocketContext';
import { DqLoading } from '../../DqLoading';
import { LastJobContent } from '../LastJobContent';

interface LastJobCardProps {
  userOrgType?: string;
  userData: AuthUserInfo;
}

export const LastJobCard: React.FC<LastJobCardProps> = props => {
  const intl = useIntl();
  const history = useHistory();
  const [mostRecentLog, setMostRecentLog] = useState<JobLog | undefined>();
  const [lastLogFetched, setLastLogFetched] = useState(false);
  const abortRef = useRef<AbortController | undefined>(undefined);
  const { request: sentinelRequest, connected } = useSocket(SocketContextSentinel, undefined);
  const { data: parsedLog } = useParsedLog(mostRecentLog?._id, mostRecentLog?.s3?.ETag);

  const fetchLatest = useCallback(
    async (signal: AbortSignal) => {
      try {
        const response = await sentinelRequest<LogSearchResult>(
          'logs search',
          {
            pageSize: 1,
          },
          { signal },
        );
        const item = response.results[0];
        if (item) {
          setMostRecentLog(item);
        }
      } finally {
        setLastLogFetched(true);
      }
    },
    [sentinelRequest],
  );

  useEffect(() => {
    if (!props.userData?.org || !connected) return;
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    fetchLatest(abort.signal);

    return () => {
      abort.abort();
    };
  }, [connected, fetchLatest, props.userData?.org]);

  useIonViewWillEnter(() => {
    if (!props.userData?.org || !connected) return;
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    fetchLatest(abortRef.current.signal);
  }, [connected, fetchLatest, props.userData?.org]);

  const onNavigate = () => {
    history.push(AppRouteConstants.LogDetails.replace(':id', mostRecentLog!._id));
  };

  const loading = intl.formatMessage(GenericMessages.loading);
  const parsing = intl.formatMessage(GenericMessages.parsing);

  return (
    <IonCard
      className="home-card"
      role="button"
      onClick={mostRecentLog ? onNavigate : undefined}
      data-testid={`recentActivityCard-lastJob`}
    >
      <IonCardContent className="home-card-content">
        <DqText variant="h3" className="home-card-title">
          {props.userOrgType === 'asset owner' ? (
            <FormattedMessage {...JobMessages.lastJobCardMostRecentShared} />
          ) : (
            <FormattedMessage {...JobMessages.lastJobCardLastUploadedJob} />
          )}
        </DqText>

        {!lastLogFetched || (lastLogFetched && mostRecentLog?._id && !parsedLog) ? (
          <DqLoading img={sentinelLogo} text={!lastLogFetched ? loading : parsing} />
        ) : parsedLog?.data && mostRecentLog ? (
          <LastJobContent
            userOrgType={props.userOrgType}
            description={mostRecentLog.description ?? mostRecentLog.name}
            status={mostRecentLog.status ?? 'complete'}
            data={parsedLog.data}
          />
        ) : (
          <div>
            <DqText variant="body-small">
              <FormattedMessage {...JobMessages.lastJobCardLogNotUploaded} />
            </DqText>
          </div>
        )}
      </IonCardContent>
    </IonCard>
  );
};
